
/**
 * Magento-parity gifts/sets tabbed PLP: hide/show product panels.
 * Tab triggers are rendered inside each tab block; this element moves
 * them into the tablist and wires keyboard / click activation.
 */

class MgPlpTabbedGrid extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#assembleTablist();

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });

    const defaultIndex = this.#defaultIndex();
    this.#activate(defaultIndex, { focus: false });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /** Move tab triggers into the dedicated tablist container. */
  #assembleTablist() {
    const nav = this.querySelector('.mg-plp-tabbed-grid__nav');
    if (!(nav instanceof HTMLElement)) return;

    const triggers = [...this.querySelectorAll('[data-mg-plp-tab-trigger]')];
    triggers.forEach((trigger, index) => {
      if (!(trigger instanceof HTMLButtonElement)) return;
      trigger.dataset.tabIndex = String(index);
      nav.appendChild(trigger);
    });
  }

  /** @returns {number} */
  #defaultIndex() {
    const tabs = this.#tabs();
    const marked = tabs.findIndex((tab) => tab.dataset.defaultActive === 'true');
    return marked >= 0 ? marked : 0;
  }

  /** @returns {HTMLButtonElement[]} */
  #tabs() {
    return [...this.querySelectorAll('[role="tab"]')].filter(
      (el) => el instanceof HTMLButtonElement
    );
  }

  /** @returns {HTMLElement[]} */
  #panels() {
    return [...this.querySelectorAll('[role="tabpanel"]')].filter(
      (el) => el instanceof HTMLElement
    );
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const tab = target.closest('[role="tab"]');
    if (!(tab instanceof HTMLButtonElement) || !this.contains(tab)) return;

    const index = Number(tab.dataset.tabIndex);
    if (Number.isNaN(index)) return;
    this.#activate(index, { focus: true });
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!(target instanceof HTMLButtonElement) || target.getAttribute('role') !== 'tab') {
      return;
    }

    const tabs = this.#tabs();
    if (tabs.length === 0) return;

    const current = tabs.indexOf(target);
    if (current < 0) return;

    let next = current;
    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(next, { focus: true });
  };

  /**
   * @param {number} index
   * @param {{ focus?: boolean }} [options]
   */
  #activate(index, options = {}) {
    const tabs = this.#tabs();
    const panels = this.#panels();
    if (tabs.length === 0) return;

    const safeIndex = Math.max(0, Math.min(index, tabs.length - 1));

    tabs.forEach((tab, i) => {
      const selected = i === safeIndex;
      tab.classList.toggle('is-active', selected);
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    });

    panels.forEach((panel, i) => {
      const selected = i === safeIndex;
      if (selected) {
        panel.hidden = false;
        panel.removeAttribute('hidden');
      } else {
        panel.hidden = true;
        panel.setAttribute('hidden', '');
      }
    });

    if (options.focus) {
      tabs[safeIndex]?.focus();
    }
  }
}

if (!customElements.get('mg-plp-tabbed-grid')) {
  customElements.define('mg-plp-tabbed-grid', MgPlpTabbedGrid);
}


